<template>
  <!-- 1 - Spinner -->
  <div class="loading" v-if="isLoading">
    <base-spinner></base-spinner>
  </div>
  <!-- 2 - No Match -->
  <NoMatch @toggleMatch="resetRocks" v-if="noMatch === true" />
  <!-- 3 - Base Card -->
  <base-card
    v-else
    v-for="rock in getAllRocks.slice(0, limit)"
    :key="rock.idName"
    :details="`/rocks/${rock.idName}`"
  >
    <!-- 3.1 - Photo -->
    <template v-slot:front>
      <base-photo
        :image-data="
          Object.assign(rock, {
            size: 'mini',
            source: '/img/rocks/' + rock.idName,
          })
        "
      />

      <div class="rock__name">{{ rock.name.propValue }}</div>
    </template>
    <!-- 3.2 - Details  -->
    <template v-slot:back>
      <!-- 3.2.1 - Type -->
      <div class="back--heading">{{ rock.type.propValue }}</div>
      <!-- 3.2.2 - Origin -->
      <div v-if="rock.neworigin.propValue" class="back--details">
        <template
          v-for="[key, value] of Object.entries(rock.neworigin.propValue)"
          :key="key"
        >
          <div>{{ key }}: {{ value }}</div>
        </template>
      </div>

      <div>
        <!-- 3.2.3 - Composition -->
        <div class="back--heading">
          {{ getCurrentLanguage === "en" ? "Composition" : "Composição" }}
        </div>
        <!-- 3.2.3.1 - Essential Minerals -->
        <div class="back--details" v-if="rock.composition_essential.propValue">
          <span>{{ rock.composition_essential.propValue }}</span>
        </div>
        <!-- 3.2.3.2 - Accessory Minerals -->
        <div class="back--details" v-if="rock.composition_additional.propValue">
          <span>+ {{ rock.composition_additional.propValue }}</span>
        </div>
        <!-- 3.2.3.3 - Other -->
        <div class="back--details" v-if="rock.composition_other.propValue">
          <span>+ {{ rock.composition_other.propValue }}</span>
        </div>
        <!-- 3.2.4 - Grains -->
        <div
          class="back--heading"
          v-if="rock.grain.min || rock.grain.max || rock.grain.other"
        >
          {{ getCurrentLanguage === "en" ? "Grains" : "Grãos" }}
        </div>
        <!-- 3.2.4.1 - Min Grains -->
        <div
          class="back--details"
          v-if="rock.grain.min || rock.grain.max || rock.grain.other"
        >
          <span v-if="rock.grain.min"
            >{{ replaceSymbols(rock.grain.min.signal) }}
            {{ rock.grain.min.value }} mm<span v-if="rock.grain.max">
              ~
            </span></span
          >
          <!-- 3.2.4.2 - Max Grains -->
          <span v-if="rock.grain.max"
            >{{ replaceSymbols(rock.grain.max.signal) }}
            {{ rock.grain.max.value }} mm</span
          >
          <!-- 3.2.4.3 - Other Grain -->
          <div v-if="rock.grain.other">{{ rock.grain.other }}</div>
        </div>
      </div>
    </template>
  </base-card>
</template>

<script setup>
import BaseCard from "@/components/ui/BaseCard.vue";
import BasePhoto from "@/components/ui/BasePhoto.vue";
import NoMatch from "@/components/finder/NoMatch.vue";

import { computed, ref, watch, onMounted, onBeforeMount } from "vue";
import { useStore } from "vuex";

const store = useStore();

// observing
let startinglimit = ref(12); // infinite scrolling
let limit = ref(); //infinite scrolling
let active = ref(true); // infinite scrolling

const isLoading = ref(false);

onBeforeMount(() => {
  getBasePage();
});

onMounted(() => {
  observing();
});

/**
 * @returns the whole page in the user language
 */
async function getBasePage() {
  isLoading.value = !isLoading.value;
  await store.dispatch("rocks/getRockNames");
  store.dispatch("rocks/getAllRocks");
  isLoading.value = !isLoading.value;
}

/**
 * function that observes where the last element is positioned for lazy loading and infinite scrolling
 */
function observing() {
  const loading = document.querySelector(".loadingStatus");

  if (
    limit.value > getAllRocks.value.length ||
    limit.value == getAllRocks.value.length
  ) {
    active.value = false;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        limit.value += 8;
      }
      if (
        limit.value > getAllRocks.value.length ||
        limit.value == getAllRocks.value.length
      ) {
        active.value = false;
      }
    });
    // console.log("limit:" + limit.value);
    // console.log("total:" + getAllRocks.value.length);
  });
  observer.observe(loading);
}

/**
 * @returns {Array} of rock Objects
 */
const getAllRocks = computed(() => {
  return store.getters["rocks/getResults"];
});

watch(getAllRocks, () => {
  active.value = true;
  limit.value = startinglimit.value;
});
/**
 * @returns {String} of the page name in the user language
 */
//grains
function replaceSymbols(inputString) {
  return inputString.replace(/=/g, "");
}
/**
 * @returns {String} of the user language
 */
const getCurrentLanguage = computed(() => {
  return store.getters.getCurrentLanguage;
});

/**
 * if noMatch is true, it toggles the No Match alert
 */
const noMatch = computed(() => {
  return store.getters["rocks/getNoMatch"];
});

/**
 * Upon closing the No Match alert, the rocks are reset
 */
function resetRocks() {
  store.dispatch("rocks/getAllRocks");
}
</script>

<style lang="css" scoped>
.loading {
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
}
.rock__name {
  text-transform: uppercase;
  text-align: center;
  font-family: var(--font-display);
  font-size: 2rem;
  font-weight: bold;
  background-color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
@media only screen and (max-width: 37em) {
  .rock__name {
    font-size: 2.8rem;
  }
}

.back--heading {
  font-size: 1.8rem;
  text-transform: uppercase;
  text-align: center;
  font-weight: bold;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}
.back--details {
  font-size: 1.4rem;
  text-align: center;

  margin-left: 1rem;
  margin-right: 1rem;
}
</style>
